/**
 * Backend-backed persistence for tenant settings (weekend rules, seasons,
 * property types, check-in day rules). Uses the same localStorage keys as
 * the legacy code so all synchronous getters keep working — the difference
 * is that writes now fire-and-forget to `/api/tenant-settings/*` so the
 * data follows the user across devices and survives a cache clear.
 *
 * See /app/docs/MIGRATION.md for the full context.
 */
import { useSyncExternalStore } from "react";
import { localApi } from "./localApi";
import { setPlatforms } from "./platforms";

// Same prefixes as before (weekendRule.js, checkinDays.js) — do NOT change,
// or previously-saved local values will be orphaned.
const PREFIX_MAP = {
  weekend_rule: "casaneo_weekend_rule_",
  season_window: "casaneo_season_window_",
  property_type: "casaneo_property_type_",
  checkin_days: "casaneo_checkin_days_",
  business_model: "business_model:",
  vat_rate: "casaneo_vat_rate_",
  personal_property: "casaneo_personal_",
};

// Small in-memory queue to coalesce concurrent writes to the same key.
const inflight = new Map();

/**
 * Push a setting to the Casanéo backend. Fire-and-forget by default — errors
 * are logged but do NOT throw, so callers keep their optimistic behaviour.
 */
export function pushTenantSetting(kind, propertyId, data) {
  const prefix = PREFIX_MAP[kind];
  if (!prefix || !propertyId) return Promise.resolve(false);
  const key = `${kind}:${propertyId}`;
  // Debounce: only keep the last write per key while any earlier one is in
  // flight — simple version, replace the pending promise.
  inflight.set(key, data);
  // Kick off (or reuse) an in-flight request. Le résultat est retourné pour
  // que l'appelant puisse prévenir l'utilisateur si le serveur n'a pas reçu
  // le réglage (sinon il croit avoir enregistré et la valeur « revient »).
  return Promise.resolve().then(async () => {
    const payload = inflight.get(key);
    if (payload === undefined) return true;
    try {
      await localApi.put(`/tenant-settings/${kind}/${encodeURIComponent(propertyId)}`, { data: payload });
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[tenant-settings] push failed for ${key}`, e?.response?.status || e?.message);
      return false;
    } finally {
      // Only clear if no newer value has been queued.
      if (inflight.get(key) === payload) inflight.delete(key);
    }
  });
}

export function deleteTenantSetting(kind, propertyId) {
  const prefix = PREFIX_MAP[kind];
  if (!prefix || !propertyId) return;
  localApi.delete(`/tenant-settings/${kind}/${encodeURIComponent(propertyId)}`).catch((e) => {
    // eslint-disable-next-line no-console
    console.warn(`[tenant-settings] delete failed`, e?.response?.status || e?.message);
  });
}

/**
 * Boot-time hydration: pull every setting from the server and write it into
 * localStorage. Any local override made after boot wins until the next
 * hydration (write-through keeps them in sync).
 *
 * If the server has NO data but localStorage has some, we upload the local
 * copy — one-time migration for existing users.
 */
export async function hydrateTenantSettings(attempt = 0) {
  let data;
  try {
    const res = await localApi.get("/tenant-settings");
    data = res.data;
  } catch (e) {
    // Not logged in / offline — retry a few times (tablets / slow networks),
    // otherwise lists would show every property as « saisonnier » until reload.
    if (attempt < 3) setTimeout(() => hydrateTenantSettings(attempt + 1), 4000 * (attempt + 1));
    return { ok: false, error: e?.response?.status || e?.message };
  }
  const grouped = data?.items || {};
  if (grouped.platforms && grouped.platforms._ && Array.isArray(grouped.platforms._.items)) {
    setPlatforms(grouped.platforms._.items, { persist: false });
  }
  let hydratedCount = 0;
  let migratedCount = 0;

  for (const [kind, prefix] of Object.entries(PREFIX_MAP)) {
    const serverMap = grouped[kind] || {};
    const hasServer = Object.keys(serverMap).length > 0;

    if (hasServer) {
      // Server wins: write server data into localStorage.
      for (const [propertyId, value] of Object.entries(serverMap)) {
        try {
          // String-valued kinds (property_type, business_model) stored as-is;
          // structured kinds serialised.
          const isString = kind === "property_type" || kind === "business_model" || kind === "vat_rate" || kind === "personal_property";
          const stored = isString ? String(value) : JSON.stringify(value);
          localStorage.setItem(prefix + propertyId, stored);
          hydratedCount += 1;
        } catch { /* quota */ }
      }
    } else {
      // Server empty for this kind — upload any local values we find so the
      // user's historic tuning survives future device switches.
      for (let i = 0; i < localStorage.length; i += 1) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(prefix)) continue;
        const propertyId = k.slice(prefix.length);
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        let value;
        if (kind === "property_type" || kind === "business_model" || kind === "personal_property") {
          value = raw;
        } else if (kind === "vat_rate") {
          value = Number(raw);
        } else {
          try { value = JSON.parse(raw); } catch { continue; }
        }
        pushTenantSetting(kind, propertyId, value);
        migratedCount += 1;
      }
    }
  }
  bumpSettingsVersion();
  return { ok: true, hydrated: hydratedCount, migrated: migratedCount };
}

// Version bumped after each hydration so React views re-read localStorage
// (property types, seasons…) instead of staying on the pre-hydration defaults.
let settingsVersion = 0;
const listeners = new Set();
function bumpSettingsVersion() {
  settingsVersion += 1;
  listeners.forEach((fn) => fn());
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export function useTenantSettingsVersion() {
  return useSyncExternalStore(subscribe, () => settingsVersion, () => settingsVersion);
}
